import { useState } from 'react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Dialog, DialogTrigger, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { formatTriggerLabel, getActionNodes, nodeLabel } from '@/components/workflow/workflow-shared';
import { usePermissions } from '@/contexts/PermissionContext';
import type { WorkflowCampaignBinding, WorkflowCampaignRule, WorkflowListItem } from '@easyflow/shared';
import type { useCampaignWorkflowDraft } from './useCampaignWorkflowDraft';

const scopeLabels = { campaigns: 'Campagne collegate', tenant: 'Autonomo tenant', unconfirmed: 'Ambito da confermare' };
export function WorkflowActionSummary({ nodes }: { nodes: unknown }) {
  const actions = getActionNodes(nodes);
  return <div className="my-2 text-sm">
    <p className="text-muted-foreground">Azioni presenti nella definizione; i rami non garantiscono una sequenza lineare né l’esecuzione di ogni azione.</p>
    {actions.length ? <ul className="list-disc pl-5">{actions.map(node => <li key={node.id}>{nodeLabel(node)}</li>)}</ul>
      : <p>Non sono disponibili azioni rappresentabili in questo riepilogo. Consulta la definizione.</p>}
  </div>;
}
const ruleReasons: Record<string, string> = {
  eligible: 'Eleggibile per questa campagna',
  explicitly_replaced: 'Sostituita esplicitamente per questa campagna',
  legacy_replacement_unconfirmed: 'Sostituzione storica da confermare',
  globally_disabled: 'Definizione della regola disabilitata',
  campaign_inactive: 'Campagna non attiva: la regola non riceve nuovi eventi',
  not_applicable: 'Non associata né ereditata',
};

export function CampaignDraftRulesSummary({ draft }: { draft: ReturnType<typeof useCampaignWorkflowDraft> }) {
  if (draft.loading) return <p role="status">Caricamento stato delle regole…</p>;
  if (draft.error) return <div role="alert">
    <p>Stato delle regole non disponibile: {draft.error}. Non equivale a un elenco vuoto.</p>
    <Button variant="outline" size="sm" onClick={draft.retry}>Riprova regole e collegamenti</Button>
  </div>;
  if (!draft.state) return <p className="text-sm text-muted-foreground">Le regole ereditate possono già applicarsi. Lo stato effettivo e le sostituzioni saranno disponibili dopo il primo salvataggio della campagna.</p>;
  return <CampaignRulesSummary rules={draft.state.rules} />;
}

export function CampaignRulesSummary({ rules }: { rules: WorkflowCampaignRule[] }) {
  return <div className="divide-y divide-border">
    {rules.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna regola semplice applicabile.</p>}
    {rules.map(rule => <div key={rule.rule_id} className="py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2"><span className="font-medium break-words">{rule.rule_name}</span>
        <Badge variant="outline">{rule.inherited ? 'Ereditata dal tenant' : 'Associata alla campagna'}</Badge>
        <Badge variant="secondary">{rule.globally_active ? 'Definizione abilitata' : 'Definizione disabilitata'}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{ruleReasons[rule.reason] ?? rule.reason}</p>
      {rule.replacement_requires_confirmation && <p className="text-xs">La sostituzione non dimostra equivalenza con il grafo. Confermala scegliendo esplicitamente il workflow oppure ripristina la regola.</p>}
    </div>)}
  </div>;
}

export function CampaignBindingInfo({ link }: { link: WorkflowCampaignBinding }) {
  return <div className="min-w-0 space-y-1">
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-medium break-words">{link.workflow_name}</span>
      <Badge variant="outline">v{link.definition_version}</Badge>
      <Badge variant="secondary">{link.managed_by_org_id ? 'Gestita dall’insegna' : 'Gestita dal tenant'}</Badge>
    </div>
    <p className="text-xs text-muted-foreground">{scopeLabels[link.admission_scope]} · Definizione {link.global_is_active ? 'abilitata' : 'disabilitata'} · Collegamento {link.dissociated_at ? 'dissociato' : link.is_active ? 'abilitato' : 'in pausa'}</p>
    <p className="text-sm">{link.effective.eligible ? 'Attivo per questa campagna. ' : ''}{link.effective.message}</p>
    <details className="text-xs">
      <summary className="cursor-pointer rounded focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">Trigger, descrizione e risultati</summary>
      <p className="mt-2">{formatTriggerLabel(link.trigger_type)}</p>
      <p>{link.workflow_description || 'Nessuna descrizione'}</p>
      <p>Esecuzioni di questo collegamento: {link.statistics.campaign_runs_count}. Totali della definizione: {link.statistics.global_runs_count}.</p>
      <p>Storiche senza attribuzione certa alla campagna: {link.statistics.definition_legacy_runs_count} (totale della definizione).</p>
      {link.statistics.last_run_status && <p>Ultimo stato registrato: {link.statistics.last_run_status === 'cancelled' ? 'Annullamento confermato per l’ultima esecuzione' : link.statistics.last_run_status === 'cancel_requested' ? 'Annullamento richiesto' : link.statistics.last_run_status}</p>}
      {link.statistics.last_run_error && <p role="alert">{link.statistics.last_run_error}</p>}
    </details>
  </div>;
}

/** Consultation does not require the distinct Builder permission. A new tab keeps the campaign draft mounted. */
export function WorkflowDefinitionLink({ definition }: { definition: WorkflowListItem | WorkflowCampaignBinding }) {
  const [open, setOpen] = useState(false);
  const { hasPermission, loading } = usePermissions();
  const name = 'workflow_name' in definition ? definition.workflow_name : definition.name;
  const id = 'workflow_id' in definition ? definition.workflow_id : definition.id;
  const description = 'workflow_description' in definition ? definition.workflow_description : definition.description;
  const pathname = window.location.pathname;
  const prefix = pathname.split('/').filter(Boolean)[0];
  const builderPath = `/${prefix}/workflow-builder?workflow=${encodeURIComponent(String(id))}&from=campaign`;
  return <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline">Consulta definizione</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>{String(name)}</DialogTitle>
          <DialogDescription>Definizione condivisa, non una copia della campagna. Le modifiche generali riguardano anche le altre campagne collegate.</DialogDescription></DialogHeader>
        <p className="text-sm">{String(description || 'Nessuna descrizione')}</p>
        <p className="text-sm">Trigger: {formatTriggerLabel(definition.trigger_type)}</p>
        <WorkflowActionSummary nodes={definition.nodes} />
        <p className="text-sm">{scopeLabels[definition.admission_scope]} · {definition.managed_by_org_id ? 'Gestione insegna: modifica, eliminazione, abilitazione generale e clone editabile protetti.' : 'Gestione tenant'}</p>
        <details><summary className="cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">Nodi della definizione (non una sequenza garantita)</summary>
          <pre className="text-xs overflow-auto max-h-52 p-2 bg-muted">{JSON.stringify(definition.nodes, null, 2)}</pre>
        </details>
        {!loading && hasPermission('integrations.view') && <Button asChild><a href={builderPath} target="_blank" rel="noopener noreferrer">Apri Builder in una nuova scheda</a></Button>}
        <p className="text-xs text-muted-foreground">La campagna e la sua bozza restano in questa scheda. Tornare dopo un salvataggio nel Builder non associa automaticamente la definizione.</p>
      </DialogContent>
    </Dialog>
  </>;
}
